import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InventoryRawMaterialViewModel } from "../../../viewmodels/inventory/rawMaterial/InventoryRawMaterialViewModel";

import { connect } from "react-redux";

import * as actions from "../../../store/actions/index";

import { withRouter } from "../../../shared/utility";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import SelectMenu from "../../../components/controls/SelectMenu";
import OperationalDataTableSkeleton from "../../../components/operational/OperationalDataTableSkeleton";
import OperationalAnalyticsStrip from "../../../components/operational/OperationalAnalyticsStrip";
import OperationalTableToolbar from "../../../components/operational/OperationalTableToolbar";
import InventoryRawMaterialSubTable from "../../../components/inventory/InventoryRawMaterialSubTable";
import Button from "../../../components/ui/Button";
import EmptyModal from "../../../components/modals/EmptyModal";
import PromoteReceivingToPackagingModal from "../../../components/inventory/PromoteReceivingToPackagingModal";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

function formatCurrency(value) {
  if (value == null || value === "") {
    return "—";
  }
  const normalized = String(value).replace(/,/g, "");
  const amount = parseFloat(normalized);
  if (Number.isNaN(amount)) {
    return `$${value}`;
  }
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatAmountInStock(row) {
  const amount =
    row.total_stock == null || row.total_stock === "" ? "—" : row.total_stock;
  const unit = row.unit_of_measure?.trim();

  if (!unit) {
    return amount;
  }

  return `${amount} ${unit}`;
}

function formatCategoryName(categoryName) {
  if (categoryName == null || categoryName === "") {
    return "Unknown";
  }
  return categoryName;
}

function bomNameTemplate(row) {
  return (
    <div>
      <div className="font-semibold text-[#0F172A]">{row.bom_name || "—"}</div>
      <div className="text-xs text-[#64748B]">PLU: {row.bom_plu || "—"}</div>
    </div>
  );
}

function InventoryRawMaterialScreen(props) {
  const { logout, addSnackbar } = props;
  const {
    getInStockRawMaterial,
    loading,
    products,
    onUpdateReceivingItemStock,
    getProductTypes,
    productTypeOptions,
    exportNetSuiteRawInventoryCsv,
    getPromoteReceivingDefaults,
    promoteReceivingToPackaging,
  } = InventoryRawMaterialViewModel(logout);

  const currentPageRef = useRef(1);
  const [filter, setFilter] = useState(-1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [expandedRows, setExpandedRows] = useState(null);
  const [promoteModal, setPromoteModal] = useState({
    open: false,
    receivingItemId: null,
  });

  const searchRef = useRef();

  useEffect(() => {
    getInStockRawMaterial(null, currentPageRef.current, filter);
    getProductTypes();
  }, []);

  const getSearchValue = () => searchRef.current?.value?.trim() || null;

  const fetchPage = (page, filterOverride = filter) => {
    getInStockRawMaterial(getSearchValue(), page, filterOverride);
  };

  const handleSearch = () => {
    currentPageRef.current = 1;
    setFirst(0);
    fetchPage(1);
  };

  const handlePagination = (event) => {
    const page = event.page + 1;
    currentPageRef.current = page;
    setFirst(event.first);
    setRows(event.rows);
    fetchPage(page);
  };

  const handleProductTypeChange = (e) => {
    const newFilter = e.target.value?.value ?? -1;
    currentPageRef.current = 1;
    setFirst(0);
    setFilter(newFilter);
    getInStockRawMaterial(getSearchValue(), 1, newFilter);
  };

  const closePromoteModal = () => {
    setPromoteModal({ open: false, receivingItemId: null });
  };

  const openPromoteModal = (receivingItemId) => {
    setPromoteModal({ open: true, receivingItemId });
  };

  const handlePromoteSuccess = () => {
    closePromoteModal();
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        "Item promoted to sellable stock",
        SnackbarType.Success,
      ),
    );
    fetchPage(currentPageRef.current);
  };

  const allowExpansion = () => true;

  const rowExpansionTemplate = (data) => (
    <InventoryRawMaterialSubTable
      data={data}
      onUpdateReceivingItemStock={onUpdateReceivingItemStock}
      onPromote={openPromoteModal}
    />
  );

  const totalRecords = products?.total ?? 0;
  const resultMeta = `${totalRecords.toLocaleString()} BOM item${totalRecords === 1 ? "" : "s"}`;

  const selectedProductType =
    productTypeOptions.find((option) => option.value === filter) ?? null;

  const analyticsStats = [
    {
      name: "Total Raw In Stock",
      stat: formatCurrency(products.totalStockDollarAmount),
      subtitle: "Across all BOMs",
      icon: "targetWeight",
    },
    {
      name: "Page Total Raw In Stock",
      stat: formatCurrency(products.totalPageStockDollarAmount),
      subtitle: "Current page",
      icon: "manufacturedWeight",
    },
  ];

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No raw material inventory matches your search or filters.
      </p>
    </div>
  );

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="space-y-5 px-4 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/inventory-raw-material"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Inventory
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Raw Material</li>
              </ol>
            </nav>
            <h1 className="mt-2 text-xl font-semibold tracking-tight text-[#0F172A]">
              In stock Raw Material
            </h1>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={exportNetSuiteRawInventoryCsv}
          >
            Export CSV
          </Button>
        </header>

        <OperationalAnalyticsStrip stats={analyticsStats} loading={loading} />

        <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <OperationalTableToolbar
            searchRow={
              <>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  name="searchRawMaterial"
                  id="searchRawMaterial"
                  className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                  placeholder="Search by BOM name or PLU..."
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </>
            }
            filterActions={
              <SelectMenu
                handleInputChange={handleProductTypeChange}
                parentClass="w-full min-w-[12rem] sm:w-56"
                placeholder="Product type"
                name="product_type"
                options={productTypeOptions}
                value={selectedProductType}
                showLabel={false}
              />
            }
            trailing={
              <span className="text-xs font-medium text-[#64748B]">
                {resultMeta}
              </span>
            }
          />

          <div className="operational-embedded-table overflow-hidden">
            {loading ? (
              <OperationalDataTableSkeleton
                columns={[
                  "BOM",
                  "Category",
                  "Amount in stock",
                  "Total Cost",
                  "",
                ]}
                rows={10}
              />
            ) : (
              <DataTable
                value={products.data ?? []}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                className="p-datatable-sm text-sm"
                emptyMessage={emptyMessage}
              >
                <Column
                  header="BOM"
                  body={bomNameTemplate}
                  style={{ minWidth: "12rem" }}
                />
                <Column
                  header="Category"
                  body={(row) => formatCategoryName(row.category_name)}
                  style={{ minWidth: "9rem" }}
                />
                <Column
                  header="Amount in stock"
                  body={(row) => formatAmountInStock(row)}
                  style={{ minWidth: "10rem", textAlign: "right" }}
                  bodyClassName="text-right text-[#0F172A]"
                />
                <Column
                  header="Total Cost"
                  body={(row) => (
                    <span className="tabular-nums font-medium text-[#0F172A]">
                      {formatCurrency(row.total_cost)}
                    </span>
                  )}
                  style={{ minWidth: "8rem", textAlign: "right" }}
                  bodyClassName="text-right tabular-nums"
                />
                <Column
                  expander={allowExpansion}
                  header="Details"
                  style={{ width: "5rem" }}
                />
              </DataTable>
            )}
          </div>

          <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
            <Paginator
              className="operational-embedded-paginator"
              first={first}
              rows={rows}
              totalRecords={totalRecords}
              onPageChange={handlePagination}
            />
          </div>
        </div>
      </div>
      <EmptyModal
        wide
        childComponent={
          promoteModal.receivingItemId ? (
            <PromoteReceivingToPackagingModal
              receivingItemId={promoteModal.receivingItemId}
              onSuccess={handlePromoteSuccess}
              onCancel={closePromoteModal}
              getPromoteReceivingDefaults={getPromoteReceivingDefaults}
              promoteReceivingToPackaging={promoteReceivingToPackaging}
              logout={logout}
            />
          ) : null
        }
        open={promoteModal.open}
        setOpen={(next) => {
          if (!next.open) {
            closePromoteModal();
          }
        }}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(InventoryRawMaterialScreen),
);
